import React, { useEffect, useState, useCallback } from 'react';
import './Pool.css';

// Account review queues: suggested follows (mining) + accounts to drop (demotions).
// When embedded in Health, both sections stack with plain-language CTAs.

const fmtAge = (iso) => {
  if (!iso) return '–';
  const ms = Date.now() - new Date(iso).getTime();
  const d = Math.floor(ms / 86400000);
  if (d > 0) return `${d}d`;
  const h = Math.floor(ms / 3600000);
  if (h > 0) return `${h}h`;
  const m = Math.floor(ms / 60000);
  return `${m}m`;
};

const fmtNum = (v) => (v == null ? '–' : Number(v).toLocaleString());

function DemotionsTable({ items, selected, toggle, onAct, busy }) {
  if (!items || items.length === 0) {
    return <div className="pool-empty">No accounts flagged to drop right now.</div>;
  }
  const allSelected = items.length > 0 && selected.size === items.length;
  return (
    <div className="pool-table-wrap">
      <div className="pool-bulk-bar">
        <button
          className="pool-btn"
          disabled={busy || selected.size === 0}
          onClick={() => onAct('approve')}
          title="Remove selected accounts from the scan list"
        >
          Drop from scan list {selected.size > 0 ? `(${selected.size})` : ''}
        </button>
        <button
          className="pool-btn pool-btn-secondary"
          disabled={busy || selected.size === 0}
          onClick={() => onAct('reject')}
        >
          Keep {selected.size > 0 ? `(${selected.size})` : ''}
        </button>
        <span className="pool-bulk-hint">
          Untouched suggestions apply automatically after about a week.
        </span>
      </div>
      <table className="pool-table">
        <thead>
          <tr>
            <th className="pool-checkbox">
              <input
                type="checkbox"
                checked={allSelected}
                onChange={() => {
                  if (allSelected) {
                    toggle('__set__', new Set());
                  } else {
                    toggle('__set__', new Set(items.map((i) => i.id)));
                  }
                }}
              />
            </th>
            <th>Handle</th>
            <th>Why</th>
            <th>Evidence</th>
            <th className="pool-num">Age</th>
          </tr>
        </thead>
        <tbody>
          {items.map((it) => (
            <tr key={it.id} className={selected.has(it.id) ? 'pool-row-selected' : ''}>
              <td>
                <input
                  type="checkbox"
                  checked={selected.has(it.id)}
                  onChange={() => toggle(it.id)}
                />
              </td>
              <td>
                <a
                  href={`https://bsky.app/profile/${it.handle}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >@{it.handle}</a>
              </td>
              <td><span className={`pool-rule pool-rule-${it.rule}`}>{it.rule}</span></td>
              <td className="pool-evidence">{it.evidence}</td>
              <td className="pool-num">{fmtAge(it.proposed_at)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function MiningTable({ items, selected, toggle, onAct, busy }) {
  if (!items || items.length === 0) {
    return (
      <div className="pool-empty">
        No new suggestions right now. Check back after the weekly account scan.
      </div>
    );
  }
  const allSelected = items.length > 0 && selected.size === items.length;
  return (
    <div className="pool-table-wrap">
      <div className="pool-bulk-bar">
        <button
          className="pool-btn"
          disabled={busy || selected.size === 0}
          onClick={() => onAct('approve')}
          title="Add selected accounts to the scan list"
        >
          Add to scan list {selected.size > 0 ? `(${selected.size})` : ''}
        </button>
        <button
          className="pool-btn pool-btn-secondary"
          disabled={busy || selected.size === 0}
          onClick={() => onAct('reject')}
        >
          Skip {selected.size > 0 ? `(${selected.size})` : ''}
        </button>
        <span className="pool-bulk-hint">
          Suggestions expire after about 30 days if untouched.
        </span>
      </div>
      <table className="pool-table">
        <thead>
          <tr>
            <th className="pool-checkbox">
              <input
                type="checkbox"
                checked={allSelected}
                onChange={() => {
                  if (allSelected) {
                    toggle('__set__', new Set());
                  } else {
                    toggle('__set__', new Set(items.map((i) => i.id)));
                  }
                }}
              />
            </th>
            <th>Account</th>
            <th>Bio</th>
            <th className="pool-num">Followers</th>
            <th className="pool-num">Posts/day</th>
            <th>Found via</th>
            <th className="pool-num">Age</th>
          </tr>
        </thead>
        <tbody>
          {items.map((it) => (
            <tr key={it.id} className={selected.has(it.id) ? 'pool-row-selected' : ''}>
              <td>
                <input
                  type="checkbox"
                  checked={selected.has(it.id)}
                  onChange={() => toggle(it.id)}
                />
              </td>
              <td>
                <a
                  href={`https://bsky.app/profile/${it.handle}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  title="Open on Bluesky"
                >@{it.handle}</a>
              </td>
              <td className="pool-evidence">{(it.bio || '').slice(0, 140)}</td>
              <td className="pool-num">{fmtNum(it.followers)}</td>
              <td className="pool-num">{it.posts_per_day != null ? it.posts_per_day.toFixed(1) : '–'}</td>
              <td>{it.proposed_by_account ? `via @${it.proposed_by_account}` : '–'}</td>
              <td className="pool-num">{fmtAge(it.proposed_at)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const Pool = ({ apiUrl, embedded = false, defaultSection = 'mining' }) => {
  const [active, setActive] = useState(defaultSection === 'demotions' ? 'demotions' : 'mining');
  const [demotions, setDemotions] = useState([]);
  const [mining, setMining] = useState([]);
  const [demSelected, setDemSelected] = useState(new Set());
  const [minSelected, setMinSelected] = useState(new Set());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [summary, setSummary] = useState({ pending_demotions: 0, mining_candidates: 0 });

  const headers = useCallback(() => {
    const tok = localStorage.getItem('admin_jwt');
    return tok ? { Authorization: `Bearer ${tok}` } : {};
  }, []);

  const load = useCallback(async () => {
    setError(null);
    try {
      const [s, d, m] = await Promise.all([
        fetch(`${apiUrl}/api/admin/pool/summary`, { headers: headers() }).then(r => r.json()),
        fetch(`${apiUrl}/api/admin/pool/demotions`, { headers: headers() }).then(r => r.json()),
        fetch(`${apiUrl}/api/admin/pool/mining-candidates`, { headers: headers() }).then(r => r.json()),
      ]);
      setSummary(s || {});
      setDemotions(d?.items || []);
      setMining(m?.items || []);
    } catch (err) {
      setError(String(err));
    }
  }, [apiUrl, headers]);

  useEffect(() => { load(); }, [load]);

  const toggleDem = (id, replacement) => {
    if (id === '__set__') { setDemSelected(replacement); return; }
    const next = new Set(demSelected);
    if (next.has(id)) next.delete(id); else next.add(id);
    setDemSelected(next);
  };
  const toggleMin = (id, replacement) => {
    if (id === '__set__') { setMinSelected(replacement); return; }
    const next = new Set(minSelected);
    if (next.has(id)) next.delete(id); else next.add(id);
    setMinSelected(next);
  };

  const decideDemotions = async (decision) => {
    setBusy(true);
    setError(null);
    try {
      const ids = Array.from(demSelected);
      const r = await fetch(`${apiUrl}/api/admin/pool/demotions/decide`, {
        method: 'POST',
        headers: { ...headers(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ ids, decision }),
      });
      if (!r.ok) throw new Error(await r.text());
      setDemSelected(new Set());
      await load();
    } catch (err) {
      setError(String(err));
    } finally {
      setBusy(false);
    }
  };

  const decideMining = async (decision) => {
    setBusy(true);
    setError(null);
    try {
      const ids = Array.from(minSelected);
      const r = await fetch(`${apiUrl}/api/admin/pool/mining-candidates/decide`, {
        method: 'POST',
        headers: { ...headers(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ ids, decision }),
      });
      if (!r.ok) throw new Error(await r.text());
      setMinSelected(new Set());
      await load();
    } catch (err) {
      setError(String(err));
    } finally {
      setBusy(false);
    }
  };

  const miningBlock = (
    <div className={embedded ? 'pool-embed-section' : null}>
      {embedded && (
        <div className="pool-embed-header">
          <h3>Suggested accounts</h3>
          <span className="pool-embed-sub">
            Open on Bluesky to explore · add to scan list or skip
            {(summary.mining_candidates || 0) > 0 && (
              <> · {summary.mining_candidates} pending</>
            )}
          </span>
        </div>
      )}
      <MiningTable
        items={mining}
        selected={minSelected}
        toggle={toggleMin}
        onAct={decideMining}
        busy={busy}
      />
    </div>
  );

  const demotionsBlock = (
    <div className={embedded ? 'pool-embed-section' : null}>
      {embedded && (
        <div className="pool-embed-header">
          <h3>Accounts to drop</h3>
          <span className="pool-embed-sub">
            Low-signal accounts proposed for removal from scanning
            {(summary.pending_demotions || 0) > 0 && (
              <> · {summary.pending_demotions} pending</>
            )}
          </span>
        </div>
      )}
      <DemotionsTable
        items={demotions}
        selected={demSelected}
        toggle={toggleDem}
        onAct={decideDemotions}
        busy={busy}
      />
    </div>
  );

  return (
    <div className={`pool-root${embedded ? ' pool-embedded' : ''}`}>
      {!embedded && (
        <div className="pool-header">
          <h2>Accounts</h2>
          <p className="pool-sub">
            Suggested people to add to scanning, and accounts that may be worth dropping.
          </p>
        </div>
      )}
      {error && <div className="pool-error">{error}</div>}

      {embedded ? (
        <>
          <div className="pool-embed-toolbar">
            <button className="pool-refresh" onClick={load} disabled={busy}>↻ Refresh accounts</button>
          </div>
          {miningBlock}
          {demotionsBlock}
        </>
      ) : (
        <>
          <div className="pool-subtabs">
            <button
              className={`pool-subtab ${active === 'mining' ? 'active' : ''}`}
              onClick={() => setActive('mining')}
            >
              Suggested accounts
              {summary.mining_candidates > 0 && (
                <span className="pool-badge">{summary.mining_candidates}</span>
              )}
            </button>
            <button
              className={`pool-subtab ${active === 'demotions' ? 'active' : ''}`}
              onClick={() => setActive('demotions')}
            >
              Accounts to drop
              {summary.pending_demotions > 0 && (
                <span className="pool-badge">{summary.pending_demotions}</span>
              )}
            </button>
            <button className="pool-refresh" onClick={load} disabled={busy}>↻ Refresh</button>
          </div>
          {active === 'mining' && miningBlock}
          {active === 'demotions' && demotionsBlock}
        </>
      )}
    </div>
  );
};

export default Pool;
